/**
 * HIST-EQUIP-01 — "which equipment did this visit touch?", rendered the same
 * way on the office 360 (History rows, DispatchPeek) and the phone's History
 * tab. Data + honesty rules live in visitEquipment.ts; this is the picture.
 *
 *   Installed on this visit   LENNOX EL16KC1-042-230A04   S/N 5826E01764 ⧉
 *   Worked on                 LENNOX 13ACXN036-230-23
 *
 * Model + serial are COPY-01 chips (click / tap / hold). When nothing is
 * tagged we say why in one quiet line instead of guessing from the notes.
 */
import { Wrench, PackagePlus } from "lucide-react";
import { cn } from "@/lib/cn";
import { CopyCode } from "@/components/Copyable";
import type { VisitEquipment, VisitUnit } from "./visitEquipment";

export function VisitEquipmentBlock({
  equipment,
  source,
  variant = "office",
  className,
}: {
  equipment: VisitEquipment | null;
  /** Where the visit came from — decides the wording of the empty line. */
  source: "esc" | "pegasus";
  /** office = 360 chrome; field = the phone's rounded card language. */
  variant?: "office" | "field";
  className?: string;
}) {
  const installed = equipment?.installed ?? [];
  const worked = equipment?.worked.filter((w) => !installed.some((i) => i.key === w.key)) ?? [];
  const field = variant === "field";

  if (installed.length === 0 && worked.length === 0) {
    return (
      <p className={cn(field ? "px-1 text-[11px] leading-snug text-slate-400" : "text-xs text-slate-400", className)}>
        {source === "esc"
          ? "No unit tagged — ESC didn't record which equipment this call touched."
          : "No unit tagged on this visit."}
      </p>
    );
  }

  return (
    <div className={cn(field ? "rounded-2xl bg-slate-50 px-3 py-2" : "rounded-lg border border-hairline bg-white px-3 py-2", className)}>
      {installed.length > 0 && (
        <UnitGroup icon={PackagePlus} label="Installed on this visit" units={installed} field={field} />
      )}
      {worked.length > 0 && (
        <UnitGroup icon={Wrench} label="Worked on" units={worked} field={field} className={installed.length > 0 ? "mt-2" : undefined} />
      )}
    </div>
  );
}

function UnitGroup({
  icon: Icon,
  label,
  units,
  field,
  className,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  units: VisitUnit[];
  field: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className={cn(
        "flex items-center gap-1.5",
        field ? "text-[9.5px] font-extrabold uppercase tracking-widest text-slate-400" : "text-[11px] font-semibold uppercase tracking-wider text-slate-400",
      )}>
        <Icon className="h-3 w-3" /> {label}
      </div>
      <ul className={cn("mt-1", field ? "space-y-1.5" : "space-y-1")}>
        {units.map((u) => (
          <li key={u.key} className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={cn("font-semibold text-slate-800", field ? "text-[13px]" : "text-sm")}>
              {u.model ? (
                <>
                  {u.mfg && <span>{u.mfg} </span>}
                  <CopyCode value={u.model} label="Copy model" variant="text" size={field ? "md" : "sm"} mono={false} className="font-semibold" />
                </>
              ) : (
                <span>{u.label}</span>
              )}
            </span>
            {u.serial && (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                S/N <CopyCode value={u.serial} label="Copy serial" />
              </span>
            )}
            {u.category && u.model && (
              <span className={cn("text-slate-400", field ? "text-[10.5px]" : "text-[11px]")}>{u.category}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
